import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import logoLight from "@/assets/logo-header.png";

const BRANCHES = {
  main: {
    label: "Catalyst Behavioral Health Consulting",
    small: "One partner. Every stage of growth.",
    home: "/",
    nav: [
      { to: "/solutions", label: "Solutions" },
      { to: "/who-we-serve", label: "Who We Serve" },
      { to: "/academy?division=bh", label: "Academy" },
      { to: "/about", label: "About" },
    ],
  },
  hr: {
    label: "Catalyst HR Consulting",
    small: "People strategy for healthcare & beyond.",
    home: "/hr",
    nav: [
      { to: "/hr#services", label: "Services" },
      { to: "/hr#industries", label: "Industries" },
      { to: "/hr#pricing", label: "Engagements" },
      { to: "/academy?category=Digital+Downloads&division=hr", label: "HR Tools" },
    ],
  },
  continuum: {
    label: "Catalyst Continuum",
    small: "Behavioral health program development.",
    home: "/continuum",
    nav: [
      { to: "/continuum#continuum", label: "The Continuum" },
      { to: "/continuum#process", label: "How We Work" },
      { to: "/academy?category=Toolkits&division=continuum", label: "Toolkits" },
    ],
  },
};

export default function SiteHeader({ branch = "main" }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const B = BRANCHES[branch] || BRANCHES.main;
  const loc = useLocation();

  return (
    <header
      className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-neutral-200"
      data-testid="site-header"
    >
      {/* Branch switcher ribbon */}
      <div className="bg-[var(--navy-deep)] text-[10px] sm:text-[11px] text-neutral-300 tracking-widest">
        <div className="mx-auto flex items-center justify-between px-[4vw] sm:px-[6vw] py-1.5 sm:py-2">
          <span className="hidden sm:inline">CATALYST NETWORK</span>
          <nav className="flex items-center gap-2 sm:gap-4 flex-wrap justify-end" data-testid="branch-switcher">
            <Link
              to="/"
              data-testid="branch-main"
              className={`transition-colors py-1 ${branch === "main" ? "text-[var(--gold)]" : "hover:text-white"}`}
            >
              BH CONSULTING
            </Link>
            <span className="opacity-30 hidden sm:inline">/</span>
            <Link
              to="/hr"
              data-testid="branch-hr"
              className={`transition-colors py-1 ${branch === "hr" ? "text-[var(--gold)]" : "hover:text-white"}`}
            >
              HR CONSULTING
            </Link>
            <span className="opacity-30 hidden sm:inline">/</span>
            <Link
              to="/continuum"
              data-testid="branch-continuum"
              className={`transition-colors py-1 ${branch === "continuum" ? "text-[var(--gold)]" : "hover:text-white"}`}
            >
              CATALYST CONTINUUM
            </Link>
          </nav>
        </div>
      </div>

      <div className="flex items-center justify-between px-[4vw] h-[72px] md:h-[96px]">
        <Link to={B.home} className="flex items-center" data-testid="brand-logo">
          <img
            src={logoLight}
            alt="Catalyst Behavioral Health Consulting, LLC — Helping Businesses Create a Stronger Foundation"
            className="h-[56px] md:h-[84px] w-auto"
            width={263}
            height={84}
          />
        </Link>

        <nav
          className={`main-nav ${open ? "open" : ""} md:flex items-center gap-7 font-semibold text-[13px] ${
            open
              ? "flex flex-col absolute top-[104px] md:top-[136px] left-0 right-0 bg-white p-6 border-b border-neutral-200 shadow-xl z-50"
              : "hidden md:flex"
          }`}
          data-testid="main-nav"
        >
          {B.nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `transition-colors hover:text-[var(--gold)] ${
                  isActive && !item.to.includes("#") ? "text-[var(--gold)]" : "text-[var(--navy)]"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.label}
            </NavLink>
          ))}

          {user && user !== false ? (
            <>
              <Link
                to="/dashboard"
                onClick={() => setOpen(false)}
                className="text-[var(--navy)] hover:text-[var(--gold)]"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
              <button
                onClick={() => { logout(); setOpen(false); }}
                className="text-[var(--navy)] hover:text-[var(--gold)] font-semibold text-[13px]"
                data-testid="nav-logout"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              className="text-[var(--navy)] hover:text-[var(--gold)]"
              data-testid="nav-login"
            >
              Sign in
            </Link>
          )}

          <Link
            to="/book"
            onClick={() => setOpen(false)}
            className={`btn btn-navy ${open ? "!py-3 !px-6 !text-sm w-full text-center mt-2" : "!py-[10px] !px-[14px] !text-[11px]"}`}
            data-testid="nav-book-cta"
          >
            Book a Consultation
          </Link>
        </nav>

        <button
          className="md:hidden text-[var(--navy)] p-2 -mr-2 inline-flex items-center justify-center min-w-[44px] min-h-[44px]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="menu-toggle"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </header>
  );
}
